import { useEffect, useRef } from 'react';
import { SetURLSearchParams } from 'react-router-dom';

const FILTERS_STORAGE_KEY = 'case_filters_session';

export const useSessionFilters = (searchParams: URLSearchParams, setSearchParams: SetURLSearchParams) => {
  const isInitialMount = useRef(true);
  const isUpdatingRef = useRef(false);
  const lastSavedFilters = useRef<Record<string, string> | null>(null);
  const isClearingRef = useRef(false);

  const saveFiltersToSession = (filters: Record<string, string>) => {
    try {
      const filtersToSave = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      if (JSON.stringify(filtersToSave) !== JSON.stringify(lastSavedFilters.current)) {
        if (Object.keys(filtersToSave).length > 0) {
          sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
          lastSavedFilters.current = filtersToSave;
        } else {
          sessionStorage.removeItem(FILTERS_STORAGE_KEY);
          lastSavedFilters.current = null;
        }
      }
    } catch (error) {
      console.error('[useSessionFilters] Error al guardar filtros en sessionStorage:', error);
    }
  };

  const loadFiltersFromSession = () => {
    try {
      const savedFilters = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        lastSavedFilters.current = parsedFilters;
        return parsedFilters;
      }
    } catch (error) {
      console.error('[useSessionFilters] Error al cargar filtros de sessionStorage:', error);
    }
    return null;
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const currentParams = Object.fromEntries(searchParams.entries());
      const savedFilters = loadFiltersFromSession();

      if (Object.keys(currentParams).length === 0 && savedFilters) {
        isUpdatingRef.current = true;
        setSearchParams(savedFilters);
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      } else if (Object.keys(currentParams).length > 0) {
        saveFiltersToSession(currentParams);
      }
    }
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && !isUpdatingRef.current) {
      const currentParams = Object.fromEntries(searchParams.entries());

      if (JSON.stringify(currentParams) !== JSON.stringify(lastSavedFilters.current)) {
        if (Object.keys(currentParams).length > 0 || isClearingRef.current) {
          saveFiltersToSession(currentParams);
        }
      }
    }
  }, [searchParams]);

  const updateFilters = (newFilters: Record<string, string>) => {
    isUpdatingRef.current = true;

    const currentFilters = Object.fromEntries(searchParams.entries());

    const baseFilters =
      Object.keys(currentFilters).length === 0 && !isClearingRef.current
        ? lastSavedFilters.current || {}
        : currentFilters;

    const clearedFilters = Object.entries(newFilters)
      .filter(([_, value]) => value === '' || value === null || value === undefined)
      .map(([key]) => key);

    const filteredBaseFilters = Object.entries(baseFilters).reduce(
      (acc, [key, value]) => {
        if (!clearedFilters.includes(key)) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    const validNewFilters = Object.entries(newFilters).reduce(
      (acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    const updatedFilters = {
      ...filteredBaseFilters,
      ...validNewFilters,
      page: '1',
    };

    setSearchParams(updatedFilters);

    saveFiltersToSession(updatedFilters);

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  const clearFilters = () => {
    try {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
      lastSavedFilters.current = null;
      isUpdatingRef.current = true;
      isClearingRef.current = true;
      setSearchParams({});
      setTimeout(() => {
        isUpdatingRef.current = false;
        isClearingRef.current = false;
      }, 0);
    } catch (error) {
      console.error('[useSessionFilters] Error al limpiar filtros:', error);
    }
  };

  return {
    updateFilters,
    clearFilters,
  };
};
